/**
 * Centralized compare plans data
 * Used by: ComparePlansPage (protected), PublicComparePlansPage (public /pricing/compare)
 * Update this file to change comparison content everywhere.
 *
 * @author Varun Pandey
 */

import {
  getPlanDisplayPrice,
  isSingerOrLiveBandCategory,
  MEMBERSHIP_PLANS,
} from '@/data/membershipPlansData';

export type BillingPeriod = 'quarterly' | 'annually';

export const COMPARE_PLANS = ['Free', 'Economy', 'Premium', 'Elite'] as const;
export type PlanKey = (typeof COMPARE_PLANS)[number];

export const COMPARE_PRICES: Record<BillingPeriod, Record<PlanKey, string>> = {
  quarterly: { Free: '₹0', Economy: '₹1,199', Premium: '₹2,999', Elite: '₹7,499' },
  annually: { Free: '₹0', Economy: '₹2,999', Premium: '₹7,999', Elite: '₹19,999' },
};

/** EMI per month (₹) for paid plans; null for Free */
export const COMPARE_EMI: Record<BillingPeriod, Record<PlanKey, number | null>> = {
  quarterly: { Free: null, Economy: 109, Premium: 105, Elite: 264 },
  annually: { Free: null, Economy: 105, Premium: 281, Elite: 703 },
};

const SINGER_LIVE_BAND_COMPARE_PRICES: Record<BillingPeriod, Record<PlanKey, string>> = {
  quarterly: { Free: '₹0', Economy: '₹2,999', Premium: '₹7,999', Elite: '₹19,999' },
  annually: { Free: '₹0', Economy: '₹7,499', Premium: '₹19,999', Elite: '₹49,999' },
};

const SINGER_LIVE_BAND_COMPARE_EMI: Record<BillingPeriod, Record<PlanKey, number | null>> = {
  quarterly: { Free: null, Economy: 273, Premium: 281, Elite: 704 },
  annually: { Free: null, Economy: 263, Premium: 702, Elite: 1757 },
};

export function getComparePricesForCategory(
  categoryName?: string | null,
): Record<BillingPeriod, Record<PlanKey, string>> {
  return isSingerOrLiveBandCategory(categoryName)
    ? SINGER_LIVE_BAND_COMPARE_PRICES
    : COMPARE_PRICES;
}

export function getCompareEmiForCategory(
  categoryName?: string | null,
): Record<BillingPeriod, Record<PlanKey, number | null>> {
  return isSingerOrLiveBandCategory(categoryName)
    ? SINGER_LIVE_BAND_COMPARE_EMI
    : COMPARE_EMI;
}

function formatCompareInrPrice(amount: number): string {
  return `₹${amount.toLocaleString()}`;
}

/** Compare header prices on native iOS (App Store display chart, category-aware). */
export function getIosComparePricesForCategory(
  categoryName?: string | null,
): Record<BillingPeriod, Record<PlanKey, string>> {
  const quarterly: Record<PlanKey, string> = {
    Free: '₹0',
    Economy: '₹0',
    Premium: '₹0',
    Elite: '₹0',
  };
  const annually: Record<PlanKey, string> = {
    Free: '₹0',
    Economy: '₹0',
    Premium: '₹0',
    Elite: '₹0',
  };

  for (const plan of MEMBERSHIP_PLANS) {
    const key = plan.name as PlanKey;
    if (key === 'Free') continue;
    quarterly[key] = formatCompareInrPrice(
      getPlanDisplayPrice(plan, 'quarterly', categoryName),
    );
    annually[key] = formatCompareInrPrice(
      getPlanDisplayPrice(plan, 'annually', categoryName),
    );
  }

  return { quarterly, annually };
}

export type CellValue = boolean | string;

export interface ComparisonRow {
  label: string;
  free: CellValue;
  economy: CellValue;
  premium: CellValue;
  elite: CellValue;
  economyAnnual?: string;
  premiumAnnual?: string;
  eliteAnnual?: string;
}

export interface ComparisonSection {
  id: string;
  title: string;
  rows: ComparisonRow[];
}

export function getCompareCell(
  row: ComparisonRow,
  plan: PlanKey,
  period: BillingPeriod
): CellValue {
  const v = plan === 'Free' ? row.free : plan === 'Economy' ? row.economy : plan === 'Premium' ? row.premium : row.elite;
  if (period !== 'annually') return v;
  if (plan === 'Economy' && row.economyAnnual !== undefined) return row.economyAnnual;
  if (plan === 'Premium' && row.premiumAnnual !== undefined) return row.premiumAnnual;
  if (plan === 'Elite' && row.eliteAnnual !== undefined) return row.eliteAnnual;
  return v;
}

export const COMPARE_SECTIONS: ComparisonSection[] = [
  {
    id: 'website',
    title: 'Website',
    rows: [
      { label: 'No. of Websites', free: '1', economy: '2', premium: '3', elite: '8' },
      { label: 'Professional Name Visibilty', free: false, economy: false, premium: true, elite: true },
      { label: 'UI/UX Design', free: 'Basic', economy: 'Advance', premium: 'Pro', elite: 'Royale' },
      { label: 'Verified Badge', free: false, economy: false, premium: true, elite: true },
      { label: 'Multiple Profiles', free: true, economy: true, premium: true, elite: true },
      { label: 'Content (Info)', free: 'Plagiarism Check', economy: '+ Proof-Read', premium: '+ LLM', elite: '+ Expert' },
      { label: 'FAQs', free: false, economy: true, premium: true, elite: true },
      { label: 'Flag Reviews', free: false, economy: false, premium: '2 per Quarter', premiumAnnual: '10 per Year', elite: 'Unlimited', eliteAnnual: 'Unlimited' },
      { label: 'Statistics', free: false, economy: false, premium: 'Basic', elite: 'Advanced' },
      { label: 'Website Publishing TAT', free: 'Waitlist', economy: 'Upto 30 days', premium: 'Upto 15 days', elite: 'Upto 5 days' },
      { label: 'Images', free: '2', economy: '4', premium: '8', elite: '10' },
      { label: 'Videos', free: '1', economy: '2', premium: '4', elite: '5' },
    ],
  },
  {
    id: 'gigs',
    title: 'Gigs',
    rows: [
      { label: 'Express Booking', free: '1 claim / month', economy: '2 claims / month', premium: '4 claims / month', elite: '8 claims / month' },
      { label: 'Job Boosts', free: false, economy: 'Min 2 / month', premium: 'Min 5 / month', elite: 'Min 10 / month' },
      { label: 'Job Notifications', free: false, economy: 'On Dashboard', premium: '+ SMS', elite: '+ WhatsApp' },
      { label: 'Job Notification Alerts', free: false, economy: '30 mins', premium: '15 mins', elite: 'Instant' },
      { label: 'Free StarCoins', free: '250', economy: false, premium: false, elite: false, economyAnnual: '3,000', premiumAnnual: '8,000', eliteAnnual: '20,000' },
      { label: 'Discount on StarCoins Top-up', free: false, economy: '25%', premium: '50%', elite: '75%' },
    ],
  },
  {
    id: 'advertisement',
    title: 'Advertisement',
    rows: [
      { label: 'Remove Ad Banners from My Website', free: false, economy: false, premium: true, elite: true },
      { label: "Display My Ad Banner (Others' Website)", free: false, economy: false, premium: false, elite: true },
      { label: 'Display My Ad Banner (Category Page)', free: false, economy: false, premium: false, elite: 'Once per Week' },
      { label: 'Profile Displayed On Homepage', free: false, economy: false, premium: false, elite: 'Once per Month' },
      { label: 'Visibility On Search Bar Suggestions', free: false, economy: false, premium: false, elite: true },
    ],
  },
  {
    id: 'marketing',
    title: 'Marketing',
    rows: [
      { label: 'Blog Post', free: false, economy: false, premium: false, elite: true, eliteAnnual: 'Once per Year' },
      { label: 'AI Grade', free: 'No Boost', economy: 'No Boost', premium: 'Power Boost', elite: 'Turbo Boost' },
      { label: 'Profile Featured in Newsletters', free: false, economy: false, premium: false, elite: 'Once per Month' },
      { label: 'Reels Collab with StarClinch', free: false, economy: false, premium: false, elite: 'Once per Year', eliteAnnual: 'Once per Year' },
      { label: 'Story Re-sharing by StarClinch', free: false, economy: false, premium: false, elite: 'Once per Year', eliteAnnual: 'Once per Year' },
      { label: 'Story added to Highlights by StarClinch', free: false, economy: false, premium: false, elite: 'Once per Year', eliteAnnual: 'Once per Year' },
    ],
  },
  {
    id: 'payments',
    title: 'Payments',
    rows: [
      { label: 'Receive Payment Online', free: true, economy: true, premium: true, elite: true },
      { label: 'Stay 100% Tax Compliant', free: true, economy: true, premium: true, elite: true },
      { label: 'Payment Tracker', free: true, economy: true, premium: true, elite: true },
      { label: 'Receive Payment in', free: '30 days', economy: '15 days', premium: '7 Days', elite: '2 Days' },
      { label: 'TAF (Technology Aggregation Fee)', free: '25%', economy: '20%', premium: '15%', elite: '10%' },
    ],
  },
  {
    id: 'support',
    title: 'Support',
    rows: [
      { label: 'Helpline', free: 'Chat', economy: '+ Email', premium: '+ Call', elite: '+ Relationship Manager' },
      { label: 'Resolution TAT', free: '7 Days', economy: 'Within 72 Hours', premium: 'Within 48 Hours', elite: 'Within 24 Hours' },
    ],
  },
];
